import React from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Image,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useFavorites, FavoriteItem } from '../context/FavoritesContext';
import { usePersistentCart } from '../context/PersistentCartContext';

const FavoritesScreen: React.FC = () => {
  const navigation = useNavigation();
  const { state, removeFromFavorites, clearFavorites } = useFavorites();
  const { addToCart } = usePersistentCart();

  const handleRemoveFavorite = (productId: number, productName: string) => {
    Alert.alert(
      'Remover Favorito',
      `¿Quieres remover "${productName}" de tus favoritos?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Remover', 
          style: 'destructive',
          onPress: () => {
            executeRemoveFavorite(productId, productName);
          }
        },
      ]
    );
  };

  const executeRemoveFavorite = async (productId: number, productName: string) => {
    try {
      await removeFromFavorites(productId);
      console.log('✅ Favorito removido:', productName);
    } catch (error) {
      console.error('❌ Error removiendo favorito:', error);
      Alert.alert('Error', 'No se pudo remover el favorito');
    }
  };

  const handleClearAllFavorites = () => {
    if (state.itemsCount === 0) return;
    
    Alert.alert(
      'Limpiar Favoritos',
      '¿Estás seguro de que quieres remover todos los favoritos?',
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Limpiar Todo', 
          style: 'destructive',
          onPress: () => {
            executeClearFavorites();
          }
        },
      ]
    );
  };

  const executeClearFavorites = async () => {
    try {
      await clearFavorites();
      console.log('✅ Todos los favoritos fueron limpiados');
    } catch (error) {
      console.error('❌ Error limpiando favoritos:', error);
      Alert.alert('Error', 'No se pudieron limpiar los favoritos');
    }
  };

  const handleAddToCart = async (item: FavoriteItem) => {
    try {
      await addToCart({
        id: item.producto.id,
        nombre: item.producto.nombre,
        precio: parseFloat(item.producto.precio),
        imagen: item.producto.imagen,
        stock: item.producto.stock,
      }, 1);
      
      Alert.alert(
        '✅ Agregado al carrito',
        `${item.producto.nombre} se agregó a tu carrito`,
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Error agregando al carrito:', error);
      Alert.alert('Error', 'No se pudo agregar al carrito');
    }
  };

  const handleProductPress = (productId: number) => {
    (navigation as any).navigate('ProductDetail', { productId });
  };

  const renderFavoriteItem = ({ item }: { item: FavoriteItem }) => (
    <View style={styles.favoriteItem}>
      <TouchableOpacity 
        onPress={() => handleProductPress(item.producto.id)}
        style={styles.productInfo}
      >
        <View style={styles.imageContainer}>
          {item.producto.imagen ? (
            <Image 
              source={{ uri: item.producto.imagen }}
              style={styles.productImage}
              onError={() => console.log('Error loading image:', item.producto.imagen)}
            />
          ) : (
            <View style={[styles.productImage, styles.placeholderImage]}>
              <Text style={styles.placeholderText}>📦</Text>
            </View>
          )}
        </View>
        
        <View style={styles.productDetails}>
          <Text style={styles.productName} numberOfLines={2}>
            {item.producto.nombre}
          </Text>
          <Text style={styles.productPrice}>
            Q{parseFloat(item.producto.precio).toFixed(2)}
          </Text>
          <Text style={styles.addedDate}>
            Agregado: {new Date(item.addedAt).toLocaleDateString('es-GT')}
          </Text>
        </View>
      </TouchableOpacity>
      
      <View style={styles.actionButtons}>
        <TouchableOpacity
          style={styles.addToCartButton}
          onPress={() => handleAddToCart(item)}
        >
          <Text style={styles.addToCartText}>🛒 Agregar</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.removeButton}
          onPress={() => handleRemoveFavorite(item.producto.id, item.producto.nombre)}
        >
          <Text style={styles.removeText}>💔 Remover</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderEmptyFavorites = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyIcon}>💔</Text>
      <Text style={styles.emptyTitle}>No tienes favoritos</Text>
      <Text style={styles.emptyDescription}>
        Agrega productos a tus favoritos para verlos aquí
      </Text>
      <TouchableOpacity
        style={styles.browseCatalogButton}
        onPress={() => (navigation as any).navigate('Search')}
      >
        <Text style={styles.browseCatalogText}>Explorar Catálogo</Text>
      </TouchableOpacity>
    </View>
  );

  if (state.isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Cargando favoritos...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Contador de favoritos */}
      {state.itemsCount > 0 && (
        <View style={styles.counterContainer}>
          <Text style={styles.counterText}>
            {state.itemsCount} {state.itemsCount === 1 ? 'favorito' : 'favoritos'}
          </Text>
          {/* Botón limpiar todo movido aquí */}
          <TouchableOpacity
            onPress={handleClearAllFavorites}
            style={styles.clearButton}
          >
            <Text style={styles.clearButtonText}>🗑️ Limpiar Todo</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Lista de favoritos */}
      {state.itemsCount === 0 ? (
        renderEmptyFavorites()
      ) : (
        <FlatList
          data={state.items}
          keyExtractor={(item) => item.id.toString()}
          renderItem={renderFavoriteItem}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.listContainer}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  counterContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  counterText: {
    fontSize: 16,
    color: '#6c757d',
    flex: 1,
  },
  clearButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#e74c3c',
    borderRadius: 20,
  },
  clearButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  listContainer: {
    padding: 16,
  },
  favoriteItem: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  productInfo: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  imageContainer: {
    marginRight: 12,
  },
  productImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
  placeholderImage: {
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderText: {
    fontSize: 24,
  },
  productDetails: {
    flex: 1,
    justifyContent: 'space-between',
  },
  productName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 4,
  },
  productPrice: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#e74c3c',
    marginBottom: 4,
  },
  addedDate: {
    fontSize: 12,
    color: '#6c757d',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  addToCartButton: {
    flex: 1,
    backgroundColor: '#27ae60',
    paddingVertical: 12,
    borderRadius: 8,
    marginRight: 8,
    alignItems: 'center',
  },
  addToCartText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  removeButton: {
    flex: 1,
    backgroundColor: '#e74c3c',
    paddingVertical: 12,
    borderRadius: 8,
    marginLeft: 8,
    alignItems: 'center',
  },
  removeText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: 16,
    color: '#6c757d',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
  },
  browseCatalogButton: {
    backgroundColor: '#e74c3c',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 25,
  },
  browseCatalogText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 18,
    color: '#6c757d',
  },
});

export default FavoritesScreen;
